import React, { useState } from "react";
import { Button, Card, Col, Form, Row, Select, Spin } from "antd";
import { generateDepositSummaryIslandExcel } from "../../Utils/DepositSummaryIslandExcel";
import { errHandler } from "../../helper/helper";
import { generateSamurdhiBankDepositSummaryReport } from "./SavingService";

const { Option } = Select;

const DepositSummaryIslandReport = ({ districtData = [], divAreaData = [] }) => {
  const [reportLevel, setReportLevel] = useState(null);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const [selectedDivisionalAreas, setSelectedDivisionalAreas] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [depositLoading, setDepositLoading] = useState(false);

  const handleReportLevelChange = (value) => {
    setReportLevel(value);
    setSelectedDistricts([]);
    setSelectedDivisionalAreas([]);
    setSelectedBranches([]);
  };

  const handleDistrictsChange = (values) => {
    if (values.includes("ALL")) {
      setSelectedDistricts(districtData.map((d) => d.districtId));
    } else {
      setSelectedDistricts(values);
    }
    setSelectedDivisionalAreas([]);
    setSelectedBranches([]);
  };

  const handleDivisionalAreasChange = (values) => {
    if (values.includes("ALL")) {
      setSelectedDivisionalAreas(getFilteredDivisionalAreas().map((d) => d.divAId));
    } else {
      setSelectedDivisionalAreas(values);
    }
    setSelectedBranches([]);
  };

  const handleBranchesChange = (values) => {
    if (values.includes("ALL")) {
      setSelectedBranches(getBranchesForSelection().map((b) => b.id));
    } else {
      setSelectedBranches(values);
    }
  };

  const getFilteredDivisionalAreas = () => {
    if (!selectedDistricts.length) return [];
    return divAreaData.filter((divArea) => selectedDistricts.includes(divArea.districtId));
  };

  const getBranchesForSelection = () => {
    if (!selectedDivisionalAreas.length) return [];
    return divAreaData
      .filter((divArea) => selectedDivisionalAreas.includes(divArea.divAId))
      .flatMap((divArea) =>
        (divArea.branchDtoList || []).map((branch) => ({
          id: branch.id,
          branchName: branch.branchName,
          divAreaName: divArea.divAIdName,
        }))
      );
  };

  const normalizeApiRecords = (rawArray) => {
    if (!Array.isArray(rawArray)) return [];
    return rawArray.map((r) => ({
      locationName: (r.locationName || "").toString().trim(),
      productName: (r.productName || "").toString().trim(),
      depositCount: Number.isFinite(Number(r.depositCount)) ? Math.trunc(Number(r.depositCount)) : 0,
      totalAmount: Number.isFinite(Number(r.totalAmount)) ? Number(r.totalAmount) : 0,
      ...r,
    }));
  };

  const extractArrayFromResponse = (res) => {
    if (!res) return [];
    const maybe = res.data ?? res;
    if (Array.isArray(maybe)) return maybe;
    const nested = maybe?.data ?? maybe?.result ?? maybe?.content ?? maybe?.rows ?? [];
    if (Array.isArray(nested)) return nested;
    return [];
  };

  const generateReport = async () => {
    let payload = { reportLevel };
    if (reportLevel === "district") payload.districtIdList = selectedDistricts;
    if (reportLevel === "division") payload.divAIdList = selectedDivisionalAreas;
    if (reportLevel === "branch") payload.branchIds = selectedBranches;

    setDepositLoading(true);

    try {
      const res = await generateSamurdhiBankDepositSummaryReport(payload);
      const rawArray = extractArrayFromResponse(res);
      const dataToUse = normalizeApiRecords(rawArray);

      if (Array.isArray(dataToUse) && dataToUse.length > 0) {
        await generateDepositSummaryIslandExcel(dataToUse, reportLevel || "report", null);
      }
    } catch (err) {
      errHandler(err);
    } finally {
      setDepositLoading(false);
    }
  };

  const isGenerateDisabled = () => {
    if (!reportLevel || depositLoading) return true;
    if (reportLevel === "district" && !selectedDistricts.length) return true;
    if (reportLevel === "division" && (!selectedDistricts.length || !selectedDivisionalAreas.length)) return true;
    if (
      reportLevel === "branch" &&
      (!selectedDistricts.length || !selectedDivisionalAreas.length || !selectedBranches.length)
    )
      return true;
    return false;
  };

  return (
    <div className="panel-container">
      <Card hoverable className="inner-card">
        <Form layout="vertical">
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item label="Select Report Level">
                <Select
                  placeholder="Select Level"
                  value={reportLevel}
                  onChange={handleReportLevelChange}
                  style={{ width: "100%" }}
                >
                  <Option value="district">District Wise</Option>
                  <Option value="division">Divisional Wise</Option>
                  <Option value="branch">Branch Wise</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Select Districts">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Select districts"
                  value={selectedDistricts}
                  onChange={handleDistrictsChange}
                  disabled={!reportLevel}
                  style={{ width: "100%" }}
                >
                  <Option value="ALL">All</Option>
                  {districtData?.map((d) => (
                    <Option key={d.districtId} value={d.districtId}>
                      {d.districtName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Select Divisional Areas">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Select divisional areas"
                  value={selectedDivisionalAreas}
                  onChange={handleDivisionalAreasChange}
                  disabled={reportLevel === "district" || !selectedDistricts.length}
                  style={{ width: "100%" }}
                >
                  <Option value="ALL">All</Option>
                  {getFilteredDivisionalAreas().map((d) => (
                    <Option key={d.divAId} value={d.divAId}>
                      {d.divAIdName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item label="Select Branches">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Select branches"
                  value={selectedBranches}
                  onChange={handleBranchesChange}
                  disabled={reportLevel !== "branch" || !selectedDivisionalAreas.length}
                  style={{ width: "100%" }}
                >
                  <Option value="ALL">All</Option>
                  {getBranchesForSelection().map((b) => (
                    <Option key={b.id} value={b.id}>
                      {b.branchName} ({b.divAreaName})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24} style={{ textAlign: "right" }}>
              <Button onClick={generateReport} disabled={isGenerateDisabled()} className="generate-btn">
                {depositLoading ? <Spin /> : "Generate Report"}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default DepositSummaryIslandReport;
